import React, { useState, useEffect } from 'react';
import { Search, Plus, Phone, Trash2, Link2, Unlink, RefreshCw, DollarSign, MapPin, Users, UserPlus, Loader2, X, DownloadCloud } from 'lucide-react';
import { phoneNumbersAPI, adminAPI, clientAPI } from '../../services/api';

const QuickAssignDialog = ({ phoneNumber, onClose, onSuccess }) => {
  const [clients, setClients] = useState([]);
  const [agents, setAgents] = useState([]);
  const [clientId, setClientId] = useState('');
  const [mode, setMode] = useState('existing'); // 'existing' | 'new'
  const [agentId, setAgentId] = useState('');
  const [newAgentName, setNewAgentName] = useState('');
  const [loadingClients, setLoadingClients] = useState(true);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await adminAPI.getClients();
        const items = Array.isArray(res.data) ? res.data : (res.data.clients || []);
        setClients(items);
      } catch (e) {
        setError('Failed to load clients');
      } finally {
        setLoadingClients(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!clientId) {
      setAgents([]);
      setAgentId('');
      return;
    }
    (async () => {
      setLoadingAgents(true);
      try {
        const res = await clientAPI.getAgents(clientId);
        setAgents(res.data.agents || []);
      } catch (e) {
        setAgents([]);
      } finally {
        setLoadingAgents(false);
      }
    })();
  }, [clientId]);

  const handleSubmit = async () => {
    setError('');
    if (!clientId) {
      setError('Please pick a client');
      return;
    }
    if (mode === 'existing' && !agentId) {
      setError('Please pick an existing agent or switch to "Create new agent"');
      return;
    }
    if (mode === 'new' && !newAgentName.trim()) {
      setError('Please enter a name for the new agent');
      return;
    }
    setSubmitting(true);
    try {
      const payload = mode === 'existing'
        ? { client_id: clientId, agent_id: agentId }
        : { client_id: clientId, new_agent_name: newAgentName.trim() };
      const res = await phoneNumbersAPI.quickAssign(phoneNumber, payload);
      onSuccess(res.data.message || 'Phone number assigned');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to assign phone number');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="quick-assign-modal">
      <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-semibold text-white">Assign to Client</h3>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-white" data-testid="quick-assign-close-btn" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-gray-500 text-sm mb-4 font-mono">{phoneNumber}</p>

        <div className="space-y-4">
          <div>
            <label className="block text-gray-300 text-sm mb-2" htmlFor="qa-client-select">Client</label>
            {loadingClients ? (
              <div className="flex items-center gap-2 text-gray-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading clients...</div>
            ) : (
              <select
                id="qa-client-select"
                value={clientId}
                onChange={(e) => { setClientId(e.target.value); setAgentId(''); }}
                className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-3 py-2 text-white outline-none focus:border-teal-400"
                data-testid="quick-assign-client-select"
              >
                <option value="">— Select a client —</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>{c.name} ({c.email})</option>
                ))}
              </select>
            )}
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => setMode('existing')}
              className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                mode === 'existing' ? 'bg-teal-400 text-black' : 'bg-[#1a1a1f] text-gray-300 hover:bg-[#252530]'
              }`}
              data-testid="quick-assign-mode-existing-btn"
            >
              <Users className="w-4 h-4 inline mr-1" /> Existing agent
            </button>
            <button
              onClick={() => setMode('new')}
              className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                mode === 'new' ? 'bg-teal-400 text-black' : 'bg-[#1a1a1f] text-gray-300 hover:bg-[#252530]'
              }`}
              data-testid="quick-assign-mode-new-btn"
            >
              <UserPlus className="w-4 h-4 inline mr-1" /> New agent
            </button>
          </div>

          {mode === 'existing' ? (
            <div>
              <label className="block text-gray-300 text-sm mb-2" htmlFor="qa-agent-select">Agent</label>
              <select
                id="qa-agent-select"
                value={agentId}
                onChange={(e) => setAgentId(e.target.value)}
                disabled={!clientId || loadingAgents}
                className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-3 py-2 text-white outline-none focus:border-teal-400 disabled:opacity-50"
                data-testid="quick-assign-agent-select"
              >
                <option value="">{loadingAgents ? 'Loading…' : (clientId ? '— Select an agent —' : 'Pick a client first')}</option>
                {agents.map((a) => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
            </div>
          ) : (
            <div>
              <label className="block text-gray-300 text-sm mb-2" htmlFor="qa-new-agent-input">New agent name</label>
              <input
                id="qa-new-agent-input"
                type="text"
                value={newAgentName}
                onChange={(e) => setNewAgentName(e.target.value)}
                placeholder="e.g., Receptionist"
                className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-3 py-2 text-white outline-none focus:border-teal-400"
                data-testid="quick-assign-new-agent-input"
              />
              <p className="text-gray-500 text-xs mt-1">A minimal inbound agent will be created under the selected client and the number wired to it.</p>
            </div>
          )}

          {error && (
            <p className="text-red-400 text-sm" data-testid="quick-assign-error">{error}</p>
          )}

          <button
            onClick={handleSubmit}
            disabled={submitting || !clientId || (mode === 'existing' ? !agentId : !newAgentName.trim())}
            className="w-full flex items-center justify-center gap-2 bg-teal-400 hover:bg-teal-500 disabled:bg-gray-700 disabled:cursor-not-allowed text-black px-4 py-2.5 rounded-lg font-medium transition-colors"
            data-testid="quick-assign-confirm-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
            Assign
          </button>
        </div>
      </div>
    </div>
  );
};

const AdminPhoneNumbers = () => {
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [areaCode, setAreaCode] = useState('');
  const [availableNumbers, setAvailableNumbers] = useState([]);
  const [selectedNumber, setSelectedNumber] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Auto-assign-on-purchase state (search modal)
  const [autoAssignEnabled, setAutoAssignEnabled] = useState(false);
  const [autoAssignClients, setAutoAssignClients] = useState([]);
  const [autoAssignClientId, setAutoAssignClientId] = useState('');
  const [autoAssignAgents, setAutoAssignAgents] = useState([]);
  const [autoAssignMode, setAutoAssignMode] = useState('existing');
  const [autoAssignAgentId, setAutoAssignAgentId] = useState('');
  const [autoAssignNewAgentName, setAutoAssignNewAgentName] = useState('');

  useEffect(() => {
    fetchPhoneNumbers();
  }, []);

  const fetchPhoneNumbers = async () => {
    setLoading(true);
    try {
      const response = await phoneNumbersAPI.getAll();
      setPhoneNumbers(response.data.phone_numbers || []);
    } catch (err) {
      console.error('Error fetching phone numbers:', err);
      setError('Failed to fetch phone numbers');
    } finally {
      setLoading(false);
    }
  };

  const syncFromTelnyx = async () => {
    setSyncing(true);
    setError('');
    try {
      const res = await phoneNumbersAPI.sync();
      setSuccess(res.data.message || 'Synced from Telnyx');
      await fetchPhoneNumbers();
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      console.error('Error syncing phone numbers:', err);
      setError(err.response?.data?.detail || 'Failed to sync from Telnyx');
    } finally {
      setSyncing(false);
    }
  };

  const searchNumbers = async () => {
    if (!areaCode || areaCode.length !== 3) {
      setError('Please enter a valid 3-digit area code');
      return;
    }
    setSearchLoading(true);
    setError('');
    try {
      const response = await phoneNumbersAPI.search({
        area_code: areaCode,
        quantity: 10
      });
      setAvailableNumbers(response.data.available_numbers || []);
      if (response.data.available_numbers?.length === 0) {
        setError('No numbers available for this area code. Try another.');
      }
    } catch (err) {
      console.error('Error searching numbers:', err);
      setError(err.response?.data?.detail || 'Failed to search numbers');
    } finally {
      setSearchLoading(false);
    }
  };

  // Lazy-load client list when auto-assign is first enabled
  useEffect(() => {
    if (!autoAssignEnabled || autoAssignClients.length > 0) return;
    (async () => {
      try {
        const res = await adminAPI.getClients();
        const items = Array.isArray(res.data) ? res.data : (res.data.clients || []);
        setAutoAssignClients(items);
      } catch {
        // silent — user can still buy without auto-assigning
      }
    })();
  }, [autoAssignEnabled, autoAssignClients.length]);

  // Refresh agent list when selected client changes
  useEffect(() => {
    if (!autoAssignClientId) {
      setAutoAssignAgents([]);
      setAutoAssignAgentId('');
      return;
    }
    (async () => {
      try {
        const res = await clientAPI.getAgents(autoAssignClientId);
        setAutoAssignAgents(res.data.agents || []);
      } catch {
        setAutoAssignAgents([]);
      }
    })();
  }, [autoAssignClientId]);

  const purchaseNumber = async (phoneNumber) => {
    setLoading(true);
    setError('');
    try {
      await phoneNumbersAPI.purchase(phoneNumber);

      // Optional one-step assignment when auto-assign is configured
      const canAutoAssign = autoAssignEnabled && autoAssignClientId && (
        (autoAssignMode === 'existing' && autoAssignAgentId) ||
        (autoAssignMode === 'new' && autoAssignNewAgentName.trim())
      );
      if (canAutoAssign) {
        try {
          const payload = autoAssignMode === 'existing'
            ? { client_id: autoAssignClientId, agent_id: autoAssignAgentId }
            : { client_id: autoAssignClientId, new_agent_name: autoAssignNewAgentName.trim() };
          const res = await phoneNumbersAPI.quickAssign(phoneNumber, payload);
          setSuccess(res.data.message || `Purchased & assigned ${phoneNumber}`);
        } catch (assignErr) {
          setSuccess(`Purchased ${phoneNumber}, but auto-assign failed: ${assignErr?.response?.data?.detail || assignErr.message}`);
        }
      } else {
        setSuccess(`Successfully purchased ${phoneNumber}`);
      }

      setShowSearchModal(false);
      setAvailableNumbers([]);
      setAreaCode('');
      fetchPhoneNumbers();
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      console.error('Error purchasing number:', err);
      setError(err.response?.data?.detail || 'Failed to purchase number');
    } finally {
      setLoading(false);
    }
  };

  const releaseNumber = async (phoneNumber) => {
    if (!window.confirm(`Are you sure you want to release ${phoneNumber}?`)) return;
    setLoading(true);
    try {
      await phoneNumbersAPI.delete(phoneNumber);
      setSuccess(`Successfully released ${phoneNumber}`);
      fetchPhoneNumbers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error releasing number:', err);
      setError(err.response?.data?.detail || 'Failed to release number');
    } finally {
      setLoading(false);
    }
  };

  const unassignNumber = async (phoneNumber) => {
    setLoading(true);
    try {
      await phoneNumbersAPI.unassign(phoneNumber);
      setSuccess(`Successfully unassigned ${phoneNumber}`);
      fetchPhoneNumbers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error unassigning number:', err);
      setError(err.response?.data?.detail || 'Failed to unassign number');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white mb-2">Phone Numbers</h1>
          <p className="text-gray-400">Purchase and manage USA virtual phone numbers via Telnyx</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={syncFromTelnyx}
            disabled={syncing || loading}
            className="flex items-center gap-2 border border-teal-500/40 text-teal-300 hover:bg-teal-500/10 disabled:opacity-50 px-4 py-2 rounded-lg transition-colors"
            data-testid="phone-numbers-sync-btn"
            title="Import numbers owned on your Telnyx account into the local DB"
          >
            {syncing ? <Loader2 className="w-5 h-5 animate-spin" /> : <DownloadCloud className="w-5 h-5" />}
            Sync from Telnyx
          </button>
          <button
            onClick={fetchPhoneNumbers}
            disabled={loading}
            className="flex items-center gap-2 border border-gray-700 text-gray-300 hover:bg-gray-800 px-4 py-2 rounded-lg transition-colors"
            data-testid="phone-numbers-refresh-btn"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={() => setShowSearchModal(true)}
            className="flex items-center gap-2 bg-teal-400 hover:bg-teal-500 text-black px-4 py-2 rounded-lg font-medium transition-colors"
            data-testid="phone-numbers-buy-btn"
          >
            <Plus className="w-5 h-5" />
            Buy Number
          </button>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400">
          {error}
          <button onClick={() => setError('')} className="float-right text-red-400 hover:text-red-300">&times;</button>
        </div>
      )}
      {success && (
        <div className="mb-6 p-4 bg-green-500/10 border border-green-500/30 rounded-lg text-green-400">
          {success}
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-teal-400/10 rounded-lg flex items-center justify-center">
              <Phone className="w-6 h-6 text-teal-400" />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Total Numbers</p>
              <p className="text-2xl font-bold text-white">{phoneNumbers.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-green-400/10 rounded-lg flex items-center justify-center">
              <Link2 className="w-6 h-6 text-green-400" />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Assigned</p>
              <p className="text-2xl font-bold text-white">
                {phoneNumbers.filter(n => n.assigned_agent_id).length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-yellow-400/10 rounded-lg flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-yellow-400" />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Monthly Cost</p>
              <p className="text-2xl font-bold text-white">
                ${(phoneNumbers.length * 5.00).toFixed(2)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Phone Numbers Table */}
      <div className="bg-[#0d1117] border border-gray-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#1a1a1f]">
              <tr>
                <th className="text-left text-gray-400 text-xs font-medium px-6 py-4">PHONE NUMBER</th>
                <th className="text-left text-gray-400 text-xs font-medium px-6 py-4">FRIENDLY NAME</th>
                <th className="text-left text-gray-400 text-xs font-medium px-6 py-4">STATUS</th>
                <th className="text-left text-gray-400 text-xs font-medium px-6 py-4">ASSIGNED TO</th>
                <th className="text-left text-gray-400 text-xs font-medium px-6 py-4">COST/MO</th>
                <th className="text-left text-gray-400 text-xs font-medium px-6 py-4">ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {phoneNumbers.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-gray-500" data-testid="phone-numbers-empty-state">
                    No phone numbers yet. Click &quot;Sync from Telnyx&quot; to import already-owned numbers, or &quot;Buy Number&quot; to purchase new ones.
                  </td>
                </tr>
              ) : (
                phoneNumbers.map((phone) => (
                  <tr key={phone.id} className="border-t border-gray-800 hover:bg-[#1a1a1f]/50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-teal-400/10 rounded-lg flex items-center justify-center">
                          <Phone className="w-5 h-5 text-teal-400" />
                        </div>
                        <span className="text-white font-mono font-medium">{phone.phone_number}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-300">{phone.friendly_name}</td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        phone.status === 'active' ? 'bg-green-400/10 text-green-400' :
                        phone.status === 'releasing' ? 'bg-yellow-400/10 text-yellow-400' :
                        'bg-gray-400/10 text-gray-400'
                      }`}>
                        {phone.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {phone.assigned_agent_name ? (
                        <span className="text-teal-400">{phone.assigned_agent_name}</span>
                      ) : (
                        <span className="text-gray-500">Unassigned</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-gray-300">${(phone.monthly_cost || 5.00).toFixed(2)}</td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        {!phone.assigned_agent_id ? (
                          <button
                            onClick={() => {
                              setSelectedNumber(phone.phone_number);
                              setShowAssignModal(true);
                            }}
                            className="p-2 hover:bg-gray-800 rounded-lg transition-colors text-gray-400 hover:text-teal-400"
                            title="Assign to Agent"
                          >
                            <Link2 className="w-5 h-5" />
                          </button>
                        ) : (
                          <button
                            onClick={() => unassignNumber(phone.phone_number)}
                            className="p-2 hover:bg-gray-800 rounded-lg transition-colors text-gray-400 hover:text-yellow-400"
                            title="Unassign"
                          >
                            <Unlink className="w-5 h-5" />
                          </button>
                        )}
                        <button
                          onClick={() => releaseNumber(phone.phone_number)}
                          className="p-2 hover:bg-gray-800 rounded-lg transition-colors text-gray-400 hover:text-red-400"
                          title="Release Number"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Search & Purchase Modal */}
      {showSearchModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-6 w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
            <h3 className="text-xl font-semibold text-white mb-6">Search & Purchase Phone Numbers</h3>
            
            {/* Search Input */}
            <div className="flex gap-3 mb-6">
              <div className="relative flex-1">
                <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type="text"
                  value={areaCode}
                  onChange={(e) => setAreaCode(e.target.value.replace(/\D/g, '').slice(0, 3))}
                  placeholder="Enter US area code (e.g., 212, 415, 310)"
                  className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg pl-12 pr-4 py-3 text-white placeholder-gray-500 outline-none focus:border-teal-400"
                  maxLength={3}
                />
              </div>
              <button
                onClick={searchNumbers}
                disabled={searchLoading || areaCode.length !== 3}
                className="flex items-center gap-2 bg-teal-400 hover:bg-teal-500 disabled:bg-teal-400/50 text-black px-6 py-3 rounded-lg font-medium transition-colors"
              >
                {searchLoading ? (
                  <RefreshCw className="w-5 h-5 animate-spin" />
                ) : (
                  <Search className="w-5 h-5" />
                )}
                Search
              </button>
            </div>

            {/* Auto-assign on purchase (collapsible) */}
            <div className="mb-4 bg-[#1a1a1f] border border-gray-800 rounded-lg">
              <button
                onClick={() => setAutoAssignEnabled((v) => !v)}
                className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-gray-200 hover:bg-[#252530] transition-colors rounded-lg"
                data-testid="auto-assign-toggle-btn"
              >
                <span className="flex items-center gap-2">
                  <Link2 className="w-4 h-4 text-teal-400" />
                  Auto-assign on purchase
                </span>
                <span className={`text-xs ${autoAssignEnabled ? 'text-teal-400' : 'text-gray-500'}`}>
                  {autoAssignEnabled ? 'ON — number will be assigned immediately after purchase' : 'OFF'}
                </span>
              </button>
              {autoAssignEnabled && (
                <div className="px-4 pb-4 pt-1 space-y-3 border-t border-gray-800">
                  <select
                    value={autoAssignClientId}
                    onChange={(e) => setAutoAssignClientId(e.target.value)}
                    className="w-full bg-[#0d1117] border border-gray-700 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-teal-400"
                    data-testid="auto-assign-client-select"
                  >
                    <option value="">— Select client —</option>
                    {autoAssignClients.map((c) => (
                      <option key={c.id} value={c.id}>{c.name} ({c.email})</option>
                    ))}
                  </select>

                  <div className="flex gap-2">
                    <button
                      onClick={() => setAutoAssignMode('existing')}
                      className={`flex-1 py-1.5 rounded text-xs font-medium transition-colors ${
                        autoAssignMode === 'existing' ? 'bg-teal-400 text-black' : 'bg-[#0d1117] text-gray-300 hover:bg-[#252530]'
                      }`}
                      data-testid="auto-assign-mode-existing-btn"
                    >
                      <Users className="w-3 h-3 inline mr-1" /> Existing agent
                    </button>
                    <button
                      onClick={() => setAutoAssignMode('new')}
                      className={`flex-1 py-1.5 rounded text-xs font-medium transition-colors ${
                        autoAssignMode === 'new' ? 'bg-teal-400 text-black' : 'bg-[#0d1117] text-gray-300 hover:bg-[#252530]'
                      }`}
                      data-testid="auto-assign-mode-new-btn"
                    >
                      <UserPlus className="w-3 h-3 inline mr-1" /> New agent
                    </button>
                  </div>

                  {autoAssignMode === 'existing' ? (
                    <select
                      value={autoAssignAgentId}
                      onChange={(e) => setAutoAssignAgentId(e.target.value)}
                      disabled={!autoAssignClientId}
                      className="w-full bg-[#0d1117] border border-gray-700 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-teal-400 disabled:opacity-50"
                      data-testid="auto-assign-agent-select"
                    >
                      <option value="">{autoAssignClientId ? '— Select agent —' : 'Pick a client first'}</option>
                      {autoAssignAgents.map((a) => (
                        <option key={a.id} value={a.id}>{a.name}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={autoAssignNewAgentName}
                      onChange={(e) => setAutoAssignNewAgentName(e.target.value)}
                      placeholder="New agent name (e.g., Receptionist)"
                      className="w-full bg-[#0d1117] border border-gray-700 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-teal-400"
                      data-testid="auto-assign-new-agent-input"
                    />
                  )}
                </div>
              )}
            </div>

            {/* Available Numbers List */}
            <div className="flex-1 overflow-y-auto space-y-2 mb-4">
              {availableNumbers.length > 0 ? (
                availableNumbers.map((num) => (
                  <div
                    key={num.phone_number}
                    className="flex items-center justify-between p-4 bg-[#1a1a1f] border border-gray-800 rounded-lg hover:border-teal-400/30 transition-colors"
                  >
                    <div>
                      <p className="text-white font-mono font-medium text-lg">{num.phone_number}</p>
                      <p className="text-gray-500 text-sm">{num.region || 'USA'}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-teal-400 font-medium">${num.monthly_cost?.toFixed(2) || '5.00'}/mo</span>
                      <button
                        onClick={() => purchaseNumber(num.phone_number)}
                        disabled={loading}
                        className="bg-teal-400 hover:bg-teal-500 disabled:bg-teal-400/50 text-black px-4 py-2 rounded-lg font-medium transition-colors"
                      >
                        {loading ? 'Purchasing...' : 'Purchase'}
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-12 text-gray-500">
                  Enter an area code and click Search to find available numbers
                </div>
              )}
            </div>

            {/* Close Button */}
            <div className="flex justify-end pt-4 border-t border-gray-800">
              <button
                onClick={() => {
                  setShowSearchModal(false);
                  setAvailableNumbers([]);
                  setAreaCode('');
                }}
                className="border border-gray-700 text-gray-300 hover:bg-gray-800 px-6 py-2 rounded-lg transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quick Assign Dialog */}
      {showAssignModal && selectedNumber && (
        <QuickAssignDialog
          phoneNumber={selectedNumber}
          onClose={() => {
            setShowAssignModal(false);
            setSelectedNumber(null);
          }}
          onSuccess={(msg) => {
            setShowAssignModal(false);
            setSelectedNumber(null);
            setSuccess(msg);
            fetchPhoneNumbers();
            setTimeout(() => setSuccess(''), 3000);
          }}
        />
      )}
    </div>
  );
};

export default AdminPhoneNumbers;
